import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { supabase } from '../supabaseClient'
import { slugifyTitle } from '../lib/helpers'
import TagInput from '../components-admin/TagInput'
import ImageUploader from '../components-admin/ImageUploader'
import TipTapEditor from '../components-admin/TipTapEditor'

export default function BlogEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [title, setTitle] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [tags, setTags] = useState([])
  const [status, setStatus] = useState('draft')
  const [publishedAt, setPublishedAt] = useState('')
  const [seoTitle, setSeoTitle] = useState('')
  const [seoDescription, setSeoDescription] = useState('')
  const [seoKeywords, setSeoKeywords] = useState('')
  const [seoImageUrl, setSeoImageUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isEdit) return
    supabase.from('posts').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return
      setTitle(data.title)
      setExcerpt(data.excerpt || '')
      setContent(data.content || '')
      setCoverImageUrl(data.cover_image_url || '')
      setTags(data.tags || [])
      setStatus(data.status || 'draft')
      setPublishedAt(data.published_at ? data.published_at.slice(0, 16) : '')
      setSeoTitle(data.seo_title || '')
      setSeoDescription(data.seo_description || '')
      setSeoKeywords(data.seo_keywords || '')
      setSeoImageUrl(data.seo_image_url || '')
    })
  }, [id, isEdit])

  const handleSave = async (e) => {
    e.preventDefault()
    setError('')
    if (!title.trim()) return setError('O título é obrigatório.')
    setSaving(true)

    const slug = slugifyTitle(title)
    const payload = {
      title, slug, excerpt, content,
      cover_image_url: coverImageUrl,
      tags, status,
      published_at: publishedAt ? new Date(publishedAt).toISOString() : null,
      seo_title: seoTitle || null,
      seo_description: seoDescription || null,
      seo_keywords: seoKeywords || null,
      seo_image_url: seoImageUrl || coverImageUrl || null,
    }

    let result
    if (isEdit) {
      result = await supabase.from('posts').update(payload).eq('id', id)
    } else {
      result = await supabase.from('posts').insert(payload).select()
    }

    if (result.error) {
      console.error('Erro ao salvar post:', result.error)
      setError(`Erro ao salvar: ${result.error.message}`)
      setSaving(false)
      return
    }

    setSaving(false)
    navigate('/admin/blog')
  }

  const inputClass = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#bef102] focus:ring-1 focus:ring-[#bef102] focus:outline-none transition-colors"
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1"

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/blog')} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-extrabold font-display text-gray-900">
          {isEdit ? 'Editar matéria' : 'Nova matéria'}
        </h1>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className={labelClass}>Título</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Resumo (excerpt)</label>
              <textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Conteúdo</label>
              <TipTapEditor value={content} onChange={setContent} />
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 h-fit shadow-sm">
            <div>
              <label className={labelClass}>Capa</label>
              <ImageUploader value={coverImageUrl} onChange={setCoverImageUrl} />
            </div>
            <div>
              <label className={labelClass}>Tags</label>
              <TagInput tags={tags} onChange={setTags} />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                <option value="draft">Rascunho</option>
                <option value="published">Publicado</option>
                <option value="scheduled">Agendado</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Data de publicação</label>
              <input type="datetime-local" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">SEO</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Meta título</label>
              <input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Meta descrição</label>
              <input value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Palavras-chave</label>
              <input value={seoKeywords} onChange={(e) => setSeoKeywords(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Imagem OG</label>
              <ImageUploader value={seoImageUrl} onChange={setSeoImageUrl} />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="rounded-lg bg-[#bef102] px-5 py-2.5 text-sm font-bold text-[#0d1f12] hover:bg-[#a8d800] transition-colors disabled:opacity-50">
            {saving ? 'Salvando…' : 'Salvar matéria'}
          </button>
          <button type="button" onClick={() => navigate('/admin/blog')} className="rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  )
}
